import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { qk } from '@/lib/query-client'
import { useWorkspace } from '@/providers/workspace-provider'
import { useAuth } from '@/providers/auth-provider'
import type { Tables, TablesInsert, TablesUpdate } from '@/types/database'

export function useCampaigns() {
  const { workspaceId } = useWorkspace()
  return useQuery({
    queryKey: qk.campaigns(workspaceId ?? 'none'),
    enabled: !!workspaceId,
    queryFn: async (): Promise<Tables<'campaigns'>[]> => {
      const { data, error } = await supabase
        .from('campaigns')
        .select('*')
        .eq('workspace_id', workspaceId!)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
      if (error) throw error
      return data ?? []
    },
  })
}

export function useUpsertCampaign() {
  const { workspaceId } = useWorkspace()
  const { user } = useAuth()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      values,
    }: {
      id?: string
      values: Omit<TablesInsert<'campaigns'>, 'workspace_id'>
    }) => {
      if (!workspaceId) throw new Error('Nenhum workspace selecionado')
      if (id) {
        const { error } = await supabase
          .from('campaigns')
          .update(values as TablesUpdate<'campaigns'>)
          .eq('id', id)
        if (error) throw error
      } else {
        const { error } = await supabase
          .from('campaigns')
          .insert({ ...values, workspace_id: workspaceId, created_by: user?.id ?? null })
        if (error) throw error
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.campaigns(workspaceId ?? 'none') }),
  })
}
